import type { ScheduleDto } from '@octopus-do/octopus-api-client'

export type ReportSchedule = ScheduleDto['schedule']

// The setting as the dialog holds it, every field filled in. `days` is
// Monday..Sunday as 1..7 for a weekly schedule and days of the month for a
// monthly one. `hour` is a whole hour on the clock named by `timezone` — the
// browser's own zone, so nine in the morning stays nine in the morning after
// the clocks change.
export type ScheduleSetting = Omit<ScheduleDto, 'nextCrawlAt'>

// The 24-hour clock is for the picker, where the hours have to sort the way
// they are read. When a crawl is named in prose — under the dialog, in the
// card's tooltip — it goes back on the 12-hour one.
const formatTime = (at: Date) => {
  const hour = at.getHours()
  const period = hour < 12 ? 'AM' : 'PM'
  const clock = hour % 12 === 0 ? 12 : hour % 12
  const minutes = String(at.getMinutes()).padStart(2, '0')

  return `${clock}:${minutes} ${period}`
}

const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()

const shiftDays = (from: Date, days: number) => {
  const shifted = new Date(from)

  shifted.setDate(shifted.getDate() + days)

  return shifted
}

// A scheduled crawl starts on the hour, so the next one reads "11:00 AM"; the
// last one is when it finished and keeps its minutes.
export const formatCrawlAt = (at: Date) => {
  const today = new Date()
  const near = [
    { day: today, name: 'Today' },
    { day: shiftDays(today, 1), name: 'Tomorrow' },
    { day: shiftDays(today, -1), name: 'Yesterday' },
  ].find(({ day }) => sameDay(at, day))

  const day =
    near?.name ??
    at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

  return `${day} at ${formatTime(at)}`
}
